import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Activity, ArrowUpRight, Atom, Brain, HeartHandshake, Loader2, ScrollText, Sparkles, Waves, Zap } from "lucide-react";
import { api } from "@/lib/auth";
import { GATES } from "@/data/temple";
import { slugify } from "@/lib/intro";
import { Page } from "@/components/app/AppKit";
import { VoiceButton } from "@/components/quantum/TeachingCards";
import { BODY_SPOTS as SPOTS, figureSrc } from "@/lib/bodyRegions";

const LAYERS = {
  physical: ["Physical", Activity], emotional: ["Emotional", HeartHandshake], metaphysical: ["Metaphysical", Sparkles], psychological: ["Psychological", Brain],
  energetic: ["Energetic", Zap], somatic: ["Somatic", Waves], philosophical: ["Philosophical", ScrollText], quantum: ["Quantum", Atom],
};
const FIG_KEY = "tt-bodymap-figure";

const Figure = ({ figure, regions, active, onPick }) => (
  <div className="relative mx-auto mt-6 w-full max-w-[360px] aspect-[848/1264] overflow-hidden">
    <AnimatePresence initial={false}>
      <motion.img key={figure} src={figureSrc(figure)} alt={`${figure === "male" ? "Male" : "Female"} figure study in gold line`} draggable={false}
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.6 }} className="absolute inset-0 w-full h-full object-cover select-none" />
    </AnimatePresence>
    {SPOTS.map(([k, x, y], i) => {
      const r = regions.find((z) => z.key === k);
      const on = active === k;
      return (
        <button key={`${k}-${i}`} type="button" data-testid={`bodymap-spot-${k}${i === 8 ? "-r" : ""}`} onClick={() => onPick(k)} aria-label={r?.name ?? k} aria-pressed={on}
          className="group absolute w-11 h-11 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center" style={{ left: `${x}%`, top: `${y}%` }}>
          <span aria-hidden className={`absolute inset-1 rounded-full transition-[transform,opacity] duration-500 ${on ? "scale-125 opacity-100" : "scale-75 opacity-0 group-hover:opacity-70"}`} style={{ background: "radial-gradient(circle, rgba(242,200,140,0.7) 0%, rgba(194,65,79,0.35) 50%, transparent 72%)" }} />
          <span className={`relative w-3 h-3 rounded-full border transition-colors ${on ? "bg-[#F2E8DF] border-[#F2E8DF]" : r?.published ? "bg-[#D9B47A]/70 border-[#F2E8DF] animate-pulse" : "border-[#D9B47A]/60 bg-[#0A0706]/60"}`} />
        </button>
      );
    })}
  </div>
);

const Reading = ({ region, layers }) => {
  const [layer, setLayer] = useState(layers[0]);
  const [label, Icon] = LAYERS[layer] ?? ["", Sparkles];
  const text = region.layers?.[layer] ?? "";
  return (
    <motion.section key={region.key} data-testid={`bodymap-region-${region.key}`} initial={{ opacity: 0.3, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mt-8 border border-[#D9B47A]/25 bg-[#120C0A] p-6">
      <p className="font-tt-mono text-[11px] uppercase tracking-[0.25em] text-[#D9B47A]">The body map</p>
      <h2 data-testid="bodymap-region-name" className="mt-2 font-display text-3xl leading-tight">{region.name}</h2>
      {region.essence && <p className="mt-2 font-display italic text-lg text-[#CFC2B6]">{region.essence}</p>}
      {Array.isArray(region.gates) && region.gates.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {region.gates.map((g) => GATES[g] && <Link key={g} to={`/app/gates/${slugify(GATES[g].name)}`} data-testid={`bodymap-gate-${g}`} className="inline-flex items-center gap-1 rounded-full border border-[#C2414F]/60 px-3 py-1.5 text-[12px] text-[#F2E8DF] hover:bg-[#C2414F] transition-colors">Gate {g + 1} · {GATES[g].name} <ArrowUpRight className="w-3 h-3" /></Link>)}
        </div>
      )}
      <div role="tablist" aria-label="Layers" className="mt-6 grid grid-cols-4 gap-1.5">
        {layers.map((k) => {
          const [l, I] = LAYERS[k] ?? [k, Sparkles];
          return (
            <button key={k} role="tab" aria-selected={layer === k} data-testid={`bodymap-layer-${k}`} onClick={() => setLayer(k)}
              className={`flex flex-col items-center gap-1.5 py-3 px-1 border text-[12px] leading-tight transition-colors ${layer === k ? "bg-[#D9B47A] border-[#D9B47A] text-[#0A0706]" : "border-[#D9B47A]/20 text-[#CFC2B6] hover:border-[#D9B47A]/60"}`}>
              <I className="w-4 h-4" strokeWidth={1.5} /><span className="truncate max-w-full">{l}</span>
            </button>
          );
        })}
      </div>
      <motion.div key={layer} initial={{ opacity: 0.4 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }} data-testid="bodymap-layer-text" className="mt-5">
        <p className="flex items-center gap-2 font-tt-mono text-[11px] uppercase tracking-[0.25em] text-[#E8838E]"><Icon className="w-3.5 h-3.5" /> {label}{text && <span className="ml-auto normal-case tracking-normal"><VoiceButton text={text} testId="bodymap-voice" /></span>}</p>
        <p className="mt-3 text-[16px] leading-relaxed text-[#F2E8DF] whitespace-pre-wrap">{text || "Ariel has not written this layer yet."}</p>
      </motion.div>
    </motion.section>
  );
};

// /app/body — tap an area of the body to read it through eight layers.
export default function GatesBodyMap() {
  const [data, setData] = useState(null);
  const [figure, setFigure] = useState(() => localStorage.getItem(FIG_KEY) || "female");
  const [active, setActive] = useState(null);
  useEffect(() => {
    api.get("/gates/body-map").then(({ data: d }) => setData(d ?? {})).catch(() => setData({}));
  }, []);
  const regions = Array.isArray(data?.regions) ? data.regions : [];
  const layers = Array.isArray(data?.layers) && data.layers.length ? data.layers : Object.keys(LAYERS);
  const region = regions.find((r) => r.key === active);
  const pickFigure = (f) => {
    setFigure(f);
    localStorage.setItem(FIG_KEY, f);
  };
  return (
    <Page testId="app-body-map" eyebrow="The body map" title={<>Every part of you is a <em className="text-[#D9B47A]">gate.</em></>}>
      <p className="mt-4 text-[15px] leading-relaxed text-[#CFC2B6]">Tap an area of the body to read it through eight layers: physical, emotional, metaphysical, psychological, energetic, somatic, philosophical and quantum.</p>
      <div className="mt-6 flex gap-2" role="radiogroup" aria-label="Figure">
        {[["female", "Female"], ["male", "Male"]].map(([k, l]) => (
          <button key={k} role="radio" aria-checked={figure === k} data-testid={`bodymap-figure-${k}`} onClick={() => pickFigure(k)} className={`rounded-full border px-5 py-2 text-[13px] transition-colors ${figure === k ? "bg-[#D9B47A] border-[#D9B47A] text-[#0A0706]" : "border-[#D9B47A]/30 text-[#CFC2B6]"}`}>{l}</button>
        ))}
      </div>
      {!data ? <Loader2 className="mt-10 w-5 h-5 animate-spin text-[#D9B47A]" /> : (
        <>
          <Figure figure={figure} regions={regions} active={active} onPick={setActive} />
          {!region && <p data-testid="bodymap-hint" className="mt-4 text-center text-[14px] text-[#A8998C]">{regions.some((r) => r.published) ? "Tap a glowing point to begin." : "Ariel is still preparing the body map. It will open here soon."}</p>}
          {region && (region.published ? <Reading key={region.key} region={region} layers={layers} /> : (
            <p data-testid="bodymap-region-pending" className="mt-8 border border-[#D9B47A]/20 p-6 text-[15px] text-[#CFC2B6]"><span className="font-display text-2xl block text-[#F2E8DF]">{region.name}</span><span className="block mt-2">Ariel is still preparing this area. It will open here soon.</span></p>
          ))}
        </>
      )}
    </Page>
  );
}
